import { useEffect, useState } from "react";
import { Box, Card, Typography } from "@mui/material";
import { homeService } from "../../api/service.ts";
import type { MatchSummary } from "../../api/types";
import { useSeason } from "../../SeasonContext.tsx";

type Outcome = "WIN" | "DRAW" | "LOSS";

function outcomeOf(m: MatchSummary["recentMatches"][number]): Outcome {
    if (m.outcome) return m.outcome;
    return m.pointsScored > m.pointsConceded ? "WIN" : m.pointsScored < m.pointsConceded ? "LOSS" : "DRAW";
}

/** Serie in corso dalle partite più recenti: es. { outcome: "WIN", length: 3 } */
function currentStreak(summary: MatchSummary): { outcome: Outcome; length: number } | null {
    const recent = summary.recentMatches ?? [];
    if (recent.length === 0) return null;
    const first = outcomeOf(recent[0]);
    let length = 0;
    for (const m of recent) {
        if (outcomeOf(m) !== first) break;
        length++;
    }
    return { outcome: first, length };
}

const STREAK_LABEL: Record<Outcome, { letter: string; one: string; many: string; color: string }> = {
    WIN: { letter: "V", one: "ultima vinta", many: "vittorie di fila", color: "success.main" },
    DRAW: { letter: "P", one: "ultima pareggiata", many: "pareggi di fila", color: "text.secondary" },
    LOSS: { letter: "S", one: "ultima persa", many: "sconfitte di fila", color: "error.main" },
};

function Tile({ label, value, hint, color }: { label: string; value: string; hint?: string; color?: string }) {
    return (
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "center", px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }} noWrap>
                {label}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", lineHeight: 1.2, color: color ?? "text.primary" }} noWrap>
                {value}
            </Typography>
            {hint && <Typography variant="caption" color="text.secondary" noWrap>{hint}</Typography>}
        </Box>
    );
}

/** Numeri della stagione selezionata: partite, set, percentuale vittorie e serie in corso */
export default function SeasonStatsStrip() {
    const { selectedSeason } = useSeason();
    const [summary, setSummary] = useState<MatchSummary | null>(null);

    useEffect(() => {
        homeService.getMatchSummary().then(setSummary).catch(() => setSummary(null));
    }, []);

    if (!summary) return null;

    if (!summary.totalMatches) {
        return (
            <Card sx={{ borderRadius: 4, px: 2, py: 1.5, color: "text.secondary" }}>
                <Typography variant="body2">
                    Stagione {selectedSeason?.code}: nessuna partita ancora giocata
                </Typography>
            </Card>
        );
    }

    const streak = currentStreak(summary);
    const drawn = summary.matchesDrawn ?? 0;
    const streakLabel = streak ? STREAK_LABEL[streak.outcome] : null;

    return (
        <Card sx={{ borderRadius: 4, px: 1, py: 1.2, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tile
                label="Partite"
                value={drawn > 0
                    ? `${summary.matchesWon}V · ${drawn}P · ${summary.matchesLost}S`
                    : `${summary.matchesWon}V · ${summary.matchesLost}S`}
                hint={`su ${summary.totalMatches}`}
            />
            <Tile label="Set" value={`${summary.totalSetsWon}–${summary.totalSetsLost}`} hint="vinti–persi" />
            <Tile label="Vittorie" value={`${Math.round(summary.winRate)}%`} color={summary.winRate >= 50 ? "success.main" : summary.matchesLost > summary.matchesWon ? "error.main" : "text.primary"} />
            {streak && streakLabel && (
                <Tile
                    label="Serie"
                    value={`${streak.length} ${streakLabel.letter}`}
                    hint={streak.length > 1 ? streakLabel.many : streakLabel.one}
                    color={streakLabel.color}
                />
            )}
        </Card>
    );
}
